/**
 * Utility to get selected table info from the explorer tree
 */

import { useDatabaseStore } from '../store/databaseStore';
import { useDatabaseUIStore } from '../store/uiStore';
import type { DatabaseExplorerNode } from '../../explorer/models/DatabaseTreeModels';

export type SelectedTableInfo = {
  name: string;
  schema: string;
  database: string;
  connectionId: string;
};

/**
 * Find a node by ID in the tree
 */
function findNodeById(id: string, nodes: DatabaseExplorerNode[]): DatabaseExplorerNode | undefined {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    if (node.children?.length) {
      const found = findNodeById(id, node.children as DatabaseExplorerNode[]);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Extract database name from node hierarchy
 */
function getDatabaseFromNode(nodeId: string, treeData: DatabaseExplorerNode[]): string | undefined {
  const node = findNodeById(nodeId, treeData);
  if (!node) return undefined;

  // If this is a database node, return its name
  if (node.type === 'database' && node.metadata?.name) {
    return node.metadata.name;
  }

  // Traverse up to find database
  let currentNode = node;
  while (currentNode && currentNode.parentId) {
    const parentNode = findNodeById(currentNode.parentId, treeData);
    if (!parentNode) break;
    if (parentNode.type === 'database' && parentNode.metadata?.name) {
      return parentNode.metadata.name;
    }
    currentNode = parentNode;
  }

  return undefined;
}

/**
 * Get the connection ID from a node ID
 */
function getConnectionIdFromNode(nodeId: string, treeData: DatabaseExplorerNode[]): string | undefined {
  const node = findNodeById(nodeId, treeData);
  if (!node) return undefined;

  // If this is a connection node, return its ID
  if (node.type === 'connection' && node.metadata?.id) {
    return node.metadata.id;
  }

  // Traverse up to find connection
  let currentNode = node;
  while (currentNode && currentNode.parentId) {
    const parentNode = findNodeById(currentNode.parentId, treeData);
    if (!parentNode) break;
    if (parentNode.type === 'connection' && parentNode.metadata?.id) {
      return parentNode.metadata.id;
    }
    currentNode = parentNode;
  }

  return undefined;
}

/**
 * Get selected table info from the explorer tree
 * Returns undefined if no table is selected
 */
export function getSelectedTableInfo(): SelectedTableInfo | undefined {
  const { selectedNodeId } = useDatabaseUIStore.getState();
  if (!selectedNodeId) return undefined;

  const { treeData } = useDatabaseStore.getState();
  const node = findNodeById(selectedNodeId, treeData);

  // Only return info if a table is selected
  if (!node || node.type !== 'table') return undefined;

  const objectDetails = node.metadata?.objectDetails;
  if (!objectDetails) return undefined;

  const database = getDatabaseFromNode(selectedNodeId, treeData);
  const connectionId = getConnectionIdFromNode(selectedNodeId, treeData);

  if (!database || !connectionId) return undefined;

  return {
    name: objectDetails.name,
    schema: objectDetails.schema || 'dbo',
    database,
    connectionId,
  };
}
